import { Toolbar, GridToolbarQuickFilter } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import StyledDataGrid from "styles/styledDataGrid";
import { useFetchRuleProcessesRule } from "hooks/useRuleConfigProcess";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Box, Button, Grid, IconButton, Stack, TextField } from "@mui/material";

import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setDialog } from "slices/appSlice";
import api from "api";
import { formErrorStrfromObj } from "utils/shared";
import LoadingBackdrop from "components/LoadingBackDrop";
import UpdateRuleSetMapDialog from "./mapping/UpdateRuleSetMapDialog";

function Process({
  tabValue,
  index,
  processList,
  refreshProcesses,
  allRuleSets,
}) {
  const [rulesList, fetchRuleProcessesRule, setrulesList] =
    useFetchRuleProcessesRule();
  const [selectedProcess, setselectedProcess] = useState(null);
  const [originalRulesList, setOriginalRulesList] = useState([]);
  const [modifiedRulesMap, setModifiedRulesMap] = useState(new Map());
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [dialogProcess, setDialogProcess] = useState(null);
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const processColumns = [
    {
      field: "ruleConfigProcessKey",
      headerName: "Process Key",
      flex: 1,
    },
    {
      field: "ruleFlowGroupName",
      headerName: "Process",
      flex: 2,
    },
    {
      field: "ruleSetCount",
      headerName: "RuleSet Count",
      flex: 1,
    },
    {
      field: "mapRuleSets",
      headerName: "Map RuleSets",
      flex: 1.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => handleOpenMappingDialog(params.row)}
        >
          Map RuleSets
        </Button>
      ),
    },
  ];

  useEffect(() => {
    if (selectedProcess) {
      fetchRuleProcessesRule(selectedProcess).then((fetchedRules) => {
        setOriginalRulesList(fetchedRules); // Store original for change tracking
      });
    }
  }, [selectedProcess]);

  useEffect(() => {
    if (processList.length > 0 && !selectedProcess) {
      setselectedProcess(processList[0].ruleConfigProcessKey);
    }
  }, [processList, selectedProcess]);

  useEffect(() => {
    setIsSaveEnabled(modifiedRulesMap.size > 0);
  }, [modifiedRulesMap]);

  const updatePriority = (key, direction) => {
    const newList = rulesList.map((r) => {
      if (r.ruleConfigRuleSetKey === key) {
        const updatedRule = { ...r, priority: r.priority + direction };
        const originalRule = originalRulesList.find(
          (rule) => rule.ruleConfigRuleSetKey === key
        );

        // Only track if it's different from the original
        if (originalRule && updatedRule.priority !== originalRule.priority) {
          setModifiedRulesMap((prev) => new Map(prev).set(key, updatedRule));
        } else {
          // If it matches original again, remove it from modified map
          setModifiedRulesMap((prev) => {
            const newMap = new Map(prev);
            newMap.delete(key);
            return newMap;
          });
        }

        return updatedRule;
      }
      return r;
    });

    newList.sort((r1, r2) => r2.priority - r1.priority);
    setrulesList(newList);
  };

  const handleSave = async () => {
    const payload = Array.from(modifiedRulesMap.values()).map((rule) => ({
      ruleConfigRuleSetKey: rule.ruleConfigRuleSetKey,
      priority: rule.priority,
    }));

    setIsLoading(true); // Show loading mask
    try {
      await api.ruleConfigProcess.updateRuleSetPriorities(
        selectedProcess,
        payload
      );
      setIsLoading(false); // Hide loading mask
      setModifiedRulesMap(new Map()); // Clear modified rules map
      dispatch(
        setDialog({
          message: "Priorities updated successfully!",
          title: "Success",
        })
      );
    } catch (error) {
      setIsLoading(false); // Hide loading mask
      dispatch(
        setDialog({
          message:
            formErrorStrfromObj(error) ||
            "Failed to retrieve the List of Release Plans",
          title: "Error",
        })
      );
    }
  };

  const handleOpenMappingDialog = (processRow) => {
    setDialogProcess(processRow);
    setShowMappingDialog(true);
    fetchRuleProcessesRule(processRow.ruleConfigProcessKey).then(
      setOriginalRulesList
    );
  };

  const handleUpdateRuleSetMap = async (processKey, addedKeys, removedKeys) => {
    setIsLoading(true);
    try {
      await api.ruleConfigProcess.updateRuleConfigProcessMatch(processKey, {
        addedKeys: addedKeys,
        removedKeys: removedKeys,
      });
      await refreshProcesses();
      if (selectedProcess === processKey) {
        await fetchRuleProcessesRule(processKey);
      }
      dispatch(
        setDialog({
          message: "Process to RuleSet mapping updated!",
          title: "Success",
        })
      );
    } catch (err) {
      dispatch(
        setDialog({ message: formErrorStrfromObj(err), title: "Error" })
      );
    } finally {
      setIsLoading(false);
      setShowMappingDialog(false);
      setDialogProcess(null);
    }
  };

  const ruleSetColumns = [
    {
      field: "position",
      headerName: "Position",
      flex: 1,
      renderCell: (params) => (
        <Stack direction={"row"}>
          <IconButton
            onClick={() => updatePriority(params.row.ruleConfigRuleSetKey, -1)}
          >
            <ArrowDownwardIcon />
          </IconButton>
          <IconButton
            onClick={() => updatePriority(params.row.ruleConfigRuleSetKey, 1)}
          >
            <ArrowUpwardIcon />
          </IconButton>
          <TextField value={params.row.priority} />
        </Stack>
      ),
    },
    {
      field: "ruleConfigRuleSetName",
      headerName: "RuleSet Name",
      flex: 1,
      renderCell: (params) => (
        <Link
          to={`/rule-builder/edit-ruleset/${params.row.ruleConfigRuleSetKey}`}
        >
          {params.value}
        </Link>
      ),
    },
  ];

  const CustomToolbar = ({ onSave, isSaveEnabled }) => (
    <Toolbar
      sx={{
        justifyContent: "space-between",
        px: 1,
        py: 0,
        alignItems: "center",
      }}
    >
      <Button variant="contained" onClick={onSave} disabled={!isSaveEnabled}>
        Save Updated Priority
      </Button>
      <GridToolbarQuickFilter
        quickFilterParser={(searchInput) =>
          searchInput.split(",").map((value) => value.trim())
        }
      />
    </Toolbar>
  );

  if (isLoading)
    return <LoadingBackdrop message="Loading Processes and RuleSets" />;

  return (
    <>
      {dialogProcess && showMappingDialog && (
        <UpdateRuleSetMapDialog
          open={showMappingDialog}
          onClose={() => {
            setShowMappingDialog(false);
            setDialogProcess(null);
          }}
          onSave={(addedKeys, removedKeys) =>
            handleUpdateRuleSetMap(
              dialogProcess.ruleConfigProcessKey,
              addedKeys,
              removedKeys
            )
          }
          allRuleSets={allRuleSets}
          mappedRuleSetKeys={originalRulesList.map(
            (r) => r.ruleConfigRuleSetKey
          )}
        />
      )}
      <div hidden={tabValue !== index}>
        <Box
          sx={{
            height: "calc(100vh - 175px)",
            display: "flex",
            gap: 1,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <StyledDataGrid
              rows={processList}
              columns={processColumns}
              getRowId={(row) => row.ruleConfigProcessKey}
              disableExtendRowFullWidth
              onRowClick={(params) =>
                setselectedProcess(params.row.ruleConfigProcessKey)
              }
              hideFooter
              rowHeight={40}
              sx={{
                "& .MuiDataGrid-row": {
                  cursor: "pointer", // Change cursor to pointer for the whole row
                },
              }}
            />
          </Box>
          {selectedProcess ? (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <StyledDataGrid
                rows={rulesList}
                columns={ruleSetColumns}
                getRowId={(row) => row.ruleConfigRuleSetKey}
                rowHeight={40}
                onRowClick={(params) =>
                  setselectedProcess(params.row.ruleConfigRuleSetKey)
                }
                slots={{
                  toolbar: () => (
                    <CustomToolbar
                      onSave={handleSave}
                      isSaveEnabled={isSaveEnabled}
                    />
                  ),
                }}
              />
            </Box>
          ) : (
            "Select Process to see rules list"
          )}
        </Box>
      </div>
    </>
  );
}

export default Process;
